import { MessageStatus } from "@common/models/message-log";
import { cn } from "@/lib/utils";
import { Badge } from "@/components/ui/badge";
import { useTranslation } from "react-i18next";
import { namespace } from "@common-config/constants";

interface StatItem {
    status: MessageStatus | 'all' | 'recurring' | 'deleted' | 'campaignId';
    label: string;
    count: number;
    color: string;
}

interface DashboardStatsProps {
    stats: Record<string, number>;
    selectedStatus: string | 'all';
    onStatusClick: (status: string | 'all') => void;
}

export function DashboardStats({ stats, selectedStatus, onStatusClick }: DashboardStatsProps) {
    const { t } = useTranslation(namespace);
    const statusItems: StatItem[] = [
        { status: 'all', label: t('dashboardStats.all'), count: stats.all || 0, color: 'bg-gray-500' },
        { status: 'pending', label: t('dashboardStats.pending'), count: stats.pending || 0, color: 'bg-yellow-500' },
        { status: 'bt-sent', label: t('dashboardStats.sending'), count: stats['bt-sent'] || 0, color: 'bg-blue-500' },
        { status: 'confirmed', label: t('dashboardStats.confirmed'), count: stats.confirmed || 0, color: 'bg-green-600' },
        { status: 'received', label: t('dashboardStats.received'), count: stats.received || 0, color: 'bg-green-500' },
        { status: 'read', label: t('dashboardStats.read'), count: stats.read || 0, color: 'bg-green-700' },
        { status: 'played', label: t('dashboardStats.played'), count: stats.played || 0, color: 'bg-purple-600' },
        { status: 'failed', label: t('dashboardStats.failed'), count: stats.failed || 0, color: 'bg-red-500' },
        { status: 'cancelled', label: t('dashboardStats.cancelled'), count: stats.cancelled || 0, color: 'bg-gray-400' },
        { status: 'recurring', label: t('dashboardStats.recurring'), count: stats.recurring || 0, color: 'bg-purple-500' },
        { status: 'deleted', label: t('dashboardStats.deleted'), count: stats.deleted || 0, color: 'bg-rose-950' },
        { status: 'campaignId', label: t('dashboardStats.campaignId'), count: stats.campaignId || 0, color: 'bg-indigo-600' },
    ];

    return (
        <div className="flex flex-wrap gap-2 py-4">
            {statusItems.map((item) => (
                <div
                    key={item.status}
                    onClick={() => onStatusClick(item.status)}
                    className={cn(
                        "cursor-pointer transition-all hover:scale-105",
                        selectedStatus === item.status ? "ring-2 ring-offset-2 ring-black dark:ring-white" : "opacity-80 hover:opacity-100"
                    )}
                >
                    <Badge className={cn("text-white px-3 py-1 text-sm flex gap-2 items-center", item.color)}>
                        <span>{item.label}</span>
                        <span className="bg-white/20 px-1.5 rounded-full text-xs min-w-[1.5em] text-center">
                            {item.count}
                        </span>
                    </Badge>
                </div>
            ))}
        </div>
    );
}
